import React, { useState } from 'react';
import { PageLayout } from '../../shared/PageLayout';
import { Card } from '../../../components/ui/Card';
import { Settings, Edit2, School, Key, CheckCircle2, AlertCircle, Trash2 } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { SettingsSection } from '../../../components/ui/SettingsSection';
import { useAppStore } from '../../../app/hooks/useAppStore';
import { useAppNavigation } from '../../../app/hooks/useAppNavigation';
import type { Classroom, TeacherAccount } from '../../../accounts';
import { prepareAccountDeletion, executeAccountDeletion, type AccountDeletionDecision, type AccountDeletionPreparation } from '../../../accounts';
import { usePasswordChange } from '../../shared/usePasswordChange';
interface SettingsPageProps {
  activeAccount: TeacherAccount;
  classrooms?: Classroom[];
  onNavigate?: (route: string) => void;
}
export function SettingsPage({
  activeAccount,
  classrooms = [],
  onNavigate
}: SettingsPageProps) {
  // Singleton store — all consumers share the same state (Zustand refactor s81)
  const {
    accounts,
    clearActiveAccount,
    updateAccountNameById,
    updateAccountEmail,
    updateTeacherDetails,
    updatePassword
  } = useAppStore();
  // Profile editing state
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [editName, setEditName] = useState(activeAccount?.displayName || '');
  const [editEmail, setEditEmail] = useState(activeAccount?.email || '');
  const [editSchool, setEditSchool] = useState(activeAccount?.schoolName || '');
  // Password change — shared hook
  const pw = usePasswordChange({
    accountId: activeAccount.id,
    passwordHash: activeAccount.passwordHash,
    role: 'teacher',
    updatePassword
  });
  const nav = useAppNavigation({
    accounts,
    activeAccount,
    isInitialized: true,
    switchAccount: () => {},
    setViewingLearner: () => {},
    loadDemoAccounts: () => {},
    clearActiveAccount
  });
  // Account deletion state
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [deletionPrep, setDeletionPrep] = useState<AccountDeletionPreparation | null>(null);
  const [deletionDecisions, setDeletionDecisions] = useState<Record<string, AccountDeletionDecision>>({});
  const handleStartDeletion = () => {
    const prep = prepareAccountDeletion(accounts, activeAccount.id);
    setDeletionPrep(prep);
    // Initialize decisions
    const initialDecisions: Record<string, AccountDeletionDecision> = {};
    prep.ownedClassrooms.forEach(c => {
      initialDecisions[c.classroom.id] = {
        classroomId: c.classroom.id,
        action: 'delete' // Default action
      };
    });
    setDeletionDecisions(initialDecisions);
    setIsDeletingAccount(true);
  };
  const handleConfirmDeletion = () => {
    if (!deletionPrep) return;
    // Ensure all classrooms have a valid decision
    const decisions = Object.values(deletionDecisions);
    const isValid = decisions.every(d => {
      if (d.action === 'transfer') return !!d.newTeacherId;
      return true;
    });
    if (!isValid) return;
    // Execute deletion
    executeAccountDeletion(accounts, activeAccount.id, decisions);
    // Clear active account and navigate to login
    clearActiveAccount();
    nav.navigateToLogin();
  };
  const handleSaveProfile = () => {
    if (editName.trim()) {
      updateAccountNameById(activeAccount.id, editName.trim());
    }
    if (editEmail.trim() !== (activeAccount.email || '')) {
      updateAccountEmail(activeAccount.id, editEmail.trim());
    }
    if (editSchool.trim() !== (activeAccount.schoolName || '')) {
      updateTeacherDetails(activeAccount.id, {
        schoolName: editSchool.trim()
      });
    }
    setIsEditingProfile(false);
  };
  return <PageLayout title="Instellingen" description="Beheer je docentaccount en voorkeuren" data-id="element-10021" data-mp="1fa25d8iq8sez">
      <div className="max-w-3xl mx-auto space-y-6" data-id="element-10022" data-mp="1aca2jjgjnlw0">
        {/* Account Info */}
        <SettingsSection title="Account Instellingen" description="Beheer je persoonlijke gegevens" icon={Settings} accentColor="bg-violet-100 text-violet-600" action={!isEditingProfile ? <Button onClick={() => {
        setEditName(activeAccount?.displayName || '');
        setEditEmail(activeAccount?.email || '');
        setEditSchool(activeAccount?.schoolName || '');
        setIsEditingProfile(true);
      }} variant="ghost" size="sm" data-id="element-10024" data-mp="fd07lf1nq1e1">
                <Edit2 className="w-4 h-4 mr-2" data-id="element-10025" data-mp="3nr8wod4z0a1g" /> Bewerk
              </Button> : null} data-id="element-10023" data-mp="1cpj2intkyw1">
          <div className="space-y-6" data-id="element-10026" data-mp="2w25sexd32zot">
            {isEditingProfile ? <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-10027" data-mp="2w03026ogkugx">
                <div data-id="element-10028" data-mp="301y25de7rbvr">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-10029" data-mp="3014qpz4b7y7a">
                    Naam
                  </label>
                  <Input variant="admin" value={editName} onChange={e => setEditName(e.target.value)} placeholder="Jouw naam" autoFocus data-id="element-10030" data-mp="2nkdk3cr5f8q2" />
                </div>
                <div data-id="element-10031" data-mp="29l42c8nao6jz">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-10032" data-mp="29ldt3yg5yhqq">
                    E-mailadres
                  </label>
                  <Input variant="admin" type="email" value={editEmail} onChange={e => setEditEmail(e.target.value)} placeholder="jouw@email.nl" data-id="element-10033" data-mp="24t2setdlmrx4" />
                </div>
                <div className="sm:col-span-2" data-id="element-10034" data-mp="1qu35pr9ncn3l">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-10035" data-mp="1lvt9reg3ligx">
                    School
                  </label>
                  <Input variant="admin" value={editSchool} onChange={e => setEditSchool(e.target.value)} placeholder="Naam van je school" data-id="element-10036" data-mp="1c74fu0qfu0qz" />
                </div>
                <div className="sm:col-span-2 flex justify-end gap-2 mt-2" data-id="element-10037" data-mp="178krxusaaevz">
                  <Button onClick={() => setIsEditingProfile(false)} variant="secondary" data-id="element-10038" data-mp="17ansv9uz5sfh">
                    Annuleren
                  </Button>
                  <button onClick={handleSaveProfile} className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl text-sm font-bold transition-all flex items-center gap-2" data-id="element-10039" data-mp="1zzcnqr5ft1is">
                    Opslaan
                  </button>
                </div>
              </div> : <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-10040" data-mp="2iq2oa97jcc8h">
                  <div data-id="element-10041" data-mp="77pow163u09q">
                    <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-10042" data-mp="76xtorgf64jz">
                      Naam
                    </label>
                    <div className="px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-slate-900 font-medium" data-id="element-10043" data-mp="gzbyjt2snrad">
                      {activeAccount?.displayName}
                    </div>
                  </div>
                  <div data-id="element-10044" data-mp="3kglpzyn8jauu">
                    <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-10045" data-mp="3kfsu5spte5rh">
                      E-mailadres
                    </label>
                    <div className="px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-slate-900 font-medium" data-id="element-10046" data-mp="3tgqnnh2mmuvt">
                      {activeAccount?.email || <span className="text-slate-400 italic" data-id="element-10047" data-mp="3pgzxnfkxcisc">
                          Niet ingesteld
                        </span>}
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t border-slate-100" data-id="element-10048" data-mp="2y7wsyyahvmub">
                  <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl border border-slate-100 max-w-sm" data-id="element-10049" data-mp="2y5aml1zcbddo">
                    <div className="p-2 bg-white rounded-lg border border-slate-200 text-slate-500 shadow-sm" data-id="element-10050" data-mp="385kxuh1teaq7">
                      <School className="w-5 h-5" data-id="element-10051" data-mp="3cw9s244vplt1" />
                    </div>
                    <div data-id="element-10052" data-mp="3gxmvy8oevexb">
                      <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-0.5" data-id="element-10053" data-mp="3gyehsrs7hyae">
                        School
                      </div>
                      <div className="font-medium text-slate-900" data-id="element-10054" data-mp="3qvsymsi1kp38">
                        {activeAccount?.schoolName || 'Niet opgegeven'}
                      </div>
                    </div>
                  </div>
                </div>
              </>}
          </div>
        </SettingsSection>

        {/* Password Change */}
        <SettingsSection title="Wachtwoord" description="Wijzig je wachtwoord" icon={Key} accentColor="bg-violet-100 text-violet-600" action={!pw.isChangingPassword ? <Button onClick={() => pw.setIsChangingPassword(true)} variant="ghost" size="sm" data-id="element-10056" data-mp="dtqfrkpbafcm">
                <Edit2 className="w-4 h-4 mr-2" data-id="element-10057" data-mp="4vejp35oepcs" /> Wijzig
              </Button> : null} data-id="element-10055" data-mp="21hw7b85f9mh8">
          {pw.isChangingPassword ? <form onSubmit={pw.handleChangePassword} className="space-y-4" data-id="element-10058" data-mp="11i9og1c9wu58">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-10059" data-mp="xjsgjfl13fmr">
                <div className="sm:col-span-2" data-id="element-10060" data-mp="siuz557uepmb">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-10061" data-mp="slqxgunlrgba">
                    Huidig wachtwoord
                  </label>
                  <Input variant="admin" type="password" value={pw.currentPassword} onChange={e => pw.setCurrentPassword(e.target.value)} placeholder="••••••••" data-id="element-10062" data-mp="3db6jzdfsgiv4" />
                </div>
                <div data-id="element-10063" data-mp="2imvhl6p18le7">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-10064" data-mp="2indmsstoa9y9">
                    Nieuw wachtwoord
                  </label>
                  <Input variant="admin" type="password" value={pw.newPassword} onChange={e => pw.setNewPassword(e.target.value)} placeholder="••••••••" data-id="element-10065" data-mp="2sj6v200czha8" />
                </div>
                <div data-id="element-10066" data-mp="3g9uijtmrcfhw">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-10067" data-mp="3g89p67c6j2lm">
                    Bevestig nieuw wachtwoord
                  </label>
                  <Input variant="admin" type="password" value={pw.confirmPassword} onChange={e => pw.setConfirmPassword(e.target.value)} placeholder="••••••••" data-id="element-10068" data-mp="1lva2xvdana5d" />
                </div>
              </div>

              {pw.passwordError && <div className="flex items-start gap-2 text-sm text-rose-700 bg-rose-50 p-3 rounded-lg border border-rose-100" data-id="element-10069" data-mp="1cpukhekvgz1">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-10070" data-mp="3irwi7i9k94xn" />
                  <p data-id="element-10071" data-mp="3nqzf2ffe04uj">{pw.passwordError}</p>
                </div>}

              {pw.passwordSuccess && <div className="flex items-start gap-2 text-sm text-emerald-700 bg-emerald-50 p-3 rounded-lg border border-emerald-100" data-id="element-10072" data-mp="39vf7uh90tb2y">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-10073" data-mp="2zvockdcmys1t" />
                  <p data-id="element-10074" data-mp="34ty4mf32dcj7">{pw.passwordSuccess}</p>
                </div>}

              <div className="flex justify-end gap-2 pt-2" data-id="element-10075" data-mp="34vsum7t6zuu5">
                <Button type="button" onClick={() => pw.resetPasswordForm()} variant="secondary" data-id="element-10076" data-mp="y4te1dr1oere">
                  Annuleren
                </Button>
                <button type="submit" className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl text-sm font-bold transition-all flex items-center gap-2" data-id="element-10077" data-mp="11mqv2tpale4k">
                  Wachtwoord Wijzigen
                </button>
              </div>
            </form> : <div className="text-sm text-slate-500" data-id="element-10078" data-mp="1kcgnja5l1ntb">
              Je wachtwoord is veilig opgeslagen. Klik op 'Wijzig' om een nieuw
              wachtwoord in te stellen.
            </div>}
        </SettingsSection>

        {/* Account Deletion */}
        <SettingsSection title="Account Verwijderen" description="Verwijder je account en beheer je klassen" icon={Trash2} accentColor="bg-rose-100 text-rose-600" data-id="element-10079" data-mp="1ak16cf24139h">
          {!isDeletingAccount ? <div data-id="element-10080" data-mp="1ybgb0ehczwnw">
              <p className="text-sm text-slate-600 mb-4" data-id="element-10081" data-mp="1yawtgyvmfaae">
                Als je je account verwijdert, verlies je toegang tot al je
                gegevens. Je kunt kiezen wat er met je klassen moet gebeuren.
              </p>
              <Button onClick={handleStartDeletion} variant="ghost" className="text-rose-600 hover:bg-rose-50 hover:text-rose-700" data-id="element-10082" data-mp="2ao0jtnscl9uu">
                Account verwijderen...
              </Button>
            </div> : <div className="space-y-6" data-id="element-10083" data-mp="2piyeinayybf3">
              <div className="p-4 bg-rose-50 border border-rose-200 rounded-xl" data-id="element-10084" data-mp="2tkjyeslx7pys">
                <h4 className="font-bold text-rose-900 mb-2" data-id="element-10085" data-mp="2tifn3plozooh">
                  Weet je zeker dat je je account wilt verwijderen?
                </h4>
                <p className="text-sm text-rose-800" data-id="element-10086" data-mp="339a70nbguc6r">
                  Dit kan niet ongedaan worden gemaakt.
                  {deletionPrep && deletionPrep.ownedClassrooms.length > 0 && <span className="block mt-2 font-bold" data-id="element-10087" data-mp="3d9uazxqdduso">
                      Je beheert {deletionPrep.ownedClassrooms.length} klassen.
                      Maak hieronder een keuze per klas:
                    </span>}
                </p>
              </div>

              {deletionPrep && deletionPrep.ownedClassrooms.length > 0 && <div className="space-y-4" data-id="element-10088" data-mp="ili9n6p2nwuo">
                  {deletionPrep.ownedClassrooms.map(c => {
              const decision = deletionDecisions[c.classroom.id];
              const hasTransferOptions = c.coTeachers.length > 0 || c.schoolColleagues.length > 0;
              return <div key={c.classroom.id} className="p-4 border border-slate-200 rounded-xl bg-white" data-id="element-10089" data-mp="3xnhbrez7ui4">
                        <div className="flex items-center justify-between mb-3" data-id="element-10090" data-mp="1fb82c6dw64la">
                          <div className="font-bold text-slate-900" data-id="element-10091" data-mp="1ba5zlfkrnaek">
                            {c.classroom.name}
                          </div>
                          <div className="text-xs text-slate-500" data-id="element-10092" data-mp="1isvvc4ann6ve">
                            {c.studentCount} leerlingen, {c.coTeachers.length}{' '}
                            co-docenten
                          </div>
                        </div>

                        <div className="space-y-2" data-id="element-10093" data-mp="1duc69v0rleyt">
                          {hasTransferOptions && <label className="flex items-start gap-3 p-2 rounded-lg hover:bg-slate-50 cursor-pointer" data-id="element-10094" data-mp="21kg5zrau0ukj">
                              <input type="radio" name={`action-${c.classroom.id}`} checked={decision?.action === 'transfer'} onChange={() => setDeletionDecisions(prev => ({
                      ...prev,
                      [c.classroom.id]: {
                        ...prev[c.classroom.id],
                        action: 'transfer',
                        newTeacherId: c.coTeachers[0]?.id || c.schoolColleagues[0]?.id
                      }
                    }))} className="mt-1 text-indigo-600" data-id="element-10095" data-mp="26jj6qzjs4emp" />
                              <div className="flex-1" data-id="element-10096" data-mp="2g8q1ln71qyqw">
                                <div className="text-sm font-medium text-slate-900" data-id="element-10097" data-mp="2bf3wqh74jz5n">
                                  Draag over aan:
                                </div>
                                {decision?.action === 'transfer' && <select value={decision.newTeacherId || ''} onChange={e => setDeletionDecisions(prev => ({
                        ...prev,
                        [c.classroom.id]: {
                          ...prev[c.classroom.id],
                          newTeacherId: e.target.value
                        }
                      }))} className="mt-2 w-full text-sm border-slate-200 rounded-lg" data-id="element-10098" data-mp="22dnly8cedv0w">
                                    {c.coTeachers.length > 0 && <optgroup label="Co-docenten" data-id="element-10099" data-mp="3ueuvxroq328d">
                                        {c.coTeachers.map(t => <option key={t.id} value={t.id} data-id="element-10100" data-mp="31760xpcpd8e">
                                            {t.name}
                                          </option>)}
                                      </optgroup>}
                                    {c.schoolColleagues.length > 0 && <optgroup label="Collega's" data-id="element-10101" data-mp="1v3d0yjyrne9c">
                                        {c.schoolColleagues.map(t => <option key={t.id} value={t.id} data-id="element-10102" data-mp="2a1f8t6ctylwe">
                                            {t.name}
                                          </option>)}
                                      </optgroup>}
                                  </select>}
                              </div>
                            </label>}

                          <label className="flex items-start gap-3 p-2 rounded-lg hover:bg-slate-50 cursor-pointer" data-id="element-10103" data-mp="2nsts7eangp3z">
                            <input type="radio" name={`action-${c.classroom.id}`} checked={decision?.action === 'delete'} onChange={() => setDeletionDecisions(prev => ({
                      ...prev,
                      [c.classroom.id]: {
                        ...prev[c.classroom.id],
                        action: 'delete',
                        newTeacherId: undefined
                      }
                    }))} className="mt-1 text-indigo-600" data-id="element-10104" data-mp="31rtjktpjri3u" />
                            <div className="text-sm font-medium text-slate-900" data-id="element-10105" data-mp="2oodzv07rzeal">
                              Verwijder klas
                            </div>
                          </label>

                          <label className="flex items-start gap-3 p-2 rounded-lg hover:bg-slate-50 cursor-pointer" data-id="element-10106" data-mp="2ep57gbsk67qc">
                            <input type="radio" name={`action-${c.classroom.id}`} checked={decision?.action === 'orphan'} onChange={() => setDeletionDecisions(prev => ({
                      ...prev,
                      [c.classroom.id]: {
                        ...prev[c.classroom.id],
                        action: 'orphan',
                        newTeacherId: undefined
                      }
                    }))} className="mt-1 text-indigo-600" data-id="element-10107" data-mp="2jmyfladf9bt9" />
                            <div className="text-sm font-medium text-slate-900" data-id="element-10108" data-mp="2wq9bp5pldw5z">
                              Bewaar als verweesd (admin handelt af)
                            </div>
                          </label>
                        </div>
                      </div>;
            })}
                </div>}

              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-10109" data-mp="18uuq1nkwce1g">
                <Button onClick={() => setIsDeletingAccount(false)} variant="secondary" data-id="element-10110" data-mp="1mlhmta73jtuw">
                  Annuleren
                </Button>
                <Button onClick={handleConfirmDeletion} className="bg-rose-600 hover:bg-rose-700 text-white px-6 py-2 rounded-xl text-sm font-bold transition-all" disabled={deletionPrep && Object.values(deletionDecisions).some(d => d.action === 'transfer' && !d.newTeacherId)} data-id="element-10111" data-mp="uua5omzc2a5u">
                  Bevestig verwijdering
                </Button>
              </div>
            </div>}
        </SettingsSection>

        {/* Klas-beheer staat in een aparte pagina (Beheer) — geen duplicatie hier. */}
        <div className="text-xs text-slate-400 text-center pt-2" data-id="element-10112" data-mp="1808nl9t9pyfg">
          Wil je klassen of leerlingen beheren?{' '}
          <button onClick={() => onNavigate?.('management')} className="text-indigo-600 hover:text-indigo-700 font-bold underline-offset-2 hover:underline" data-id="element-10113" data-mp="1gyu4z1n077en">
            Ga naar Beheer →
          </button>
        </div>
      </div>
    </PageLayout>;
}